import { getInjector, getRootScope } from '@/services/angularjs-bridge';

interface AngularLocation {
    path: (path?: string) => AngularLocation | string;
    search: {
        (): Record<string, unknown>;
        (search: string): AngularLocation;
        (search: string, paramValue: unknown): AngularLocation;
    };
}

function angularLocation(): AngularLocation | undefined {
    try {
        return getInjector().get('$location') as AngularLocation;
    } catch {
        return undefined;
    }
}

function updateLocation(update: (location: AngularLocation) => void) {
    try {
        const injector = getInjector();
        const location = injector.get('$location') as AngularLocation;
        const timeout = injector.get('$timeout') as ((fn: () => void) => void) | undefined;
        const run = () => update(location);
        if (typeof timeout === 'function') {
            timeout(run);
        } else {
            run();
        }
    } catch {
        // Storybook and unit tests have no Angular injector.
    }
}

export function readGroupId(): string | undefined {
    const location = angularLocation();
    if (!location) {
        return undefined;
    }
    const search = location.search();
    const groupId = search?.groupId;
    if (groupId == null || groupId === '') {
        return undefined;
    }
    return String(groupId);
}

export function currentOrganizationId(): number | string | undefined {
    try {
        const root = getRootScope(getInjector()) as { organization?: { id?: number | string } };
        return root?.organization?.id;
    } catch {
        return undefined;
    }
}

export function isHopscotchTourActive() {
    const tour = (window as Window & {
        hopscotch?: { getCurrTour?: () => unknown };
    }).hopscotch;
    return Boolean(tour?.getCurrTour?.());
}

const USER_GROUP_TOAST_KEY = 'lax.userGroups.toast';

export function rememberUserGroupToast(messageKey: string) {
    try {
        sessionStorage.setItem(USER_GROUP_TOAST_KEY, messageKey);
    } catch {
        // Private mode can reject storage; navigation still continues.
    }
}

export function takeUserGroupToast(): string | undefined {
    try {
        const messageKey = sessionStorage.getItem(USER_GROUP_TOAST_KEY);
        if (!messageKey) {
            return undefined;
        }
        sessionStorage.removeItem(USER_GROUP_TOAST_KEY);
        return messageKey;
    } catch {
        return undefined;
    }
}

export function goToUserGroups() {
    updateLocation((location) => {
        location.path('/user-groups');
        location.search('');
    });
}

export function goToAddUserGroup(groupId?: number | string) {
    updateLocation((location) => {
        location.path('/user-group-add');
        if (groupId == null || groupId === '') {
            location.search('');
        } else {
            location.search('groupId', groupId);
        }
    });
}
